import React, { useState } from 'react';
import { Table } from './styles';
import THead from './THead';
import TBody from './TBody';
import { OnChangeSortType } from '../../business/admin/visitors/types';
import Loader from '../loadData/Loader';
import { loaderExtraStyle, StyledLoaderContainer } from './styles';
import {
  ColumnValues,
  OrderValues,
  SortConfig,
  TableColumns,
  TableRows,
} from './types';
import { NullableNumber, NullableString } from '../types';

const emptySort = { column: null, order: null };

const getNewSortConfig: (
  currentColumn: ColumnValues,
  currentOrder: OrderValues,
  clickedColumn: string,
  defaultValue: SortConfig
) => SortConfig = (
  currentColumn,
  currentOrder,
  clickedColumn,
  defaultValue
) => {
  if (currentColumn !== clickedColumn) {
    return { column: clickedColumn, order: 'desc' };
  }
  if (currentOrder === 'desc') {
    return { column: clickedColumn, order: 'asc' };
  }
  return emptySort;
};

export interface DataTableTypes {
  columns: TableColumns;
  rows: TableRows;
  onChangeSort?: OnChangeSortType;
  defaultSort?: SortConfig;
  rest?: { [key: string]: string };
  EmptyTableComponent?: React.FC;
  loading?: boolean;
  onRowClick?: (id: NullableNumber | NullableString) => void;
}

const DataTable: React.FC<DataTableTypes> = ({
  columns = [],
  rows = [],
  onChangeSort,
  defaultSort = emptySort,
  EmptyTableComponent,
  loading,
  onRowClick,
  ...rest
}: DataTableTypes) => {
  const [state, setState] = useState(defaultSort);
  const onClickColumn = (column: string): void => {
    const sortConfig = getNewSortConfig(
      state.column,
      state.order,
      column,
      defaultSort
    );
    onChangeSort && onChangeSort(sortConfig);
    setState(sortConfig);
  };

  return (
    <Table {...rest} data-testid="data-table">
      <THead columns={columns} onClickColumn={onClickColumn} state={state} />
      {loading ? (
        <tbody>
          <tr>
            <td colSpan={columns.length}>
              <StyledLoaderContainer>
                <Loader extraStyle={loaderExtraStyle} />
              </StyledLoaderContainer>
            </td>
          </tr>
        </tbody>
      ) : (
        <TBody
          onRowClick={onRowClick}
          columns={columns}
          rows={rows}
          EmptyTableComponent={EmptyTableComponent}
        />
      )}
    </Table>
  );
};

export default DataTable;
